import React, { useEffect, useRef } from 'react'
import { createNativeStackNavigator } from '@react-navigation/native-stack'
import { useCurrentUserStore } from '@store'
import { AuthNavigator } from './AuthNavigator'
import { TabNavigator } from './TabNavigator'
import { AppNavigationProp, RootStackParamList } from './../navigationTypes'
import { ProfileNavigator } from './ProfileNavigator'
import { MenuNavigator } from './MenuNavigator'
import { ContactNavigator } from './ContactNavigator'
import MessagesStackNavigator from './MessagesStackNavigator'
import {
  DemoSwitch,
  DetailsGhostProfile,
  LinkedInCallback,
  MyTicket,
  NotFound,
  PublicEvent,
  PublicPrivacyPolicy,
  SimulatedPayment,
} from '@screens'
import { useExtension } from '@hooks'
import { useFcm } from '../../hooks/useFcm'
import { getStateFromPath, useNavigation } from '@react-navigation/native'
import { linkingConfig } from '../web/linkingConfig'
import { getPendingPathFromUrl } from '../web/pendingPath'

const RootStack = createNativeStackNavigator<RootStackParamList>()

// A profile link shared from the app (QR code, "Copier le lien"): /profile/<uuid>.
const UUID = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}'
const SHARED_PROFILE_PATH = new RegExp(`^/profile/(${UUID})/?$`, 'i')
const INVITED_BY = new RegExp(`^${UUID}$`, 'i')

const getSharedProfileIdFromUrl = (): string | null => {
  if (typeof window === 'undefined') return null
  return window.location?.pathname?.match(SHARED_PROFILE_PATH)?.[1] ?? null
}

// Legal pages are public information (a store listing or an e-mail links to
// them): a direct address opens them without login.
const PUBLIC_PAGE_PATH = /^\/(legal-notice|privacy-policy|contact-us|join-us)\/?$/

// The event pages a visitor uses without an account (QR code / invitation
// link, its privacy page, the LinkedIn return, the local demo switch). The
// visitor becomes logged in ON these pages (sign-up to the event): they are
// never a page "to come back to after login", which would reload them in the
// middle of the sign-up.
const isPublicEventPath = (pathname: string): boolean =>
  pathname.startsWith('/e/') ||
  pathname === '/privacy' ||
  pathname.startsWith('/linkedin/') ||
  pathname === '/demo'

// The link of an invitation to join: /auth/register?invitedBy=<the inviter's account id>.
const getInvitedByFromUrl = (): string | null => {
  if (typeof window === 'undefined') return null
  if (!window.location?.pathname?.includes('/auth/register')) return null
  const invitedBy = new URLSearchParams(window.location.search ?? '').get('invitedBy')
  return invitedBy && INVITED_BY.test(invitedBy) ? invitedBy : null
}

export const MainNavigator = () => {
  const { userID } = useCurrentUserStore()
  const { isExtension, hash } = useExtension()
  const navigation = useNavigation<AppNavigationProp>()
  const isUserLogged = !!userID
  // Shared profile link opened while logged out: the visitor goes through Auth
  // first, then lands on that profile once logged in (or signed up). The link
  // of an invitation to join leads to the inviter's profile the same way, even
  // for someone already logged in.
  const pendingProfileIdRef = useRef(
    getInvitedByFromUrl() ?? (isUserLogged ? null : getSharedProfileIdFromUrl()),
  )
  // Any other page of the app opened while logged out: the visitor lands on
  // it once logged in. Not a legal page nor a public event page: they are
  // displayed without login.
  const pendingPathRef = useRef(
    isUserLogged ||
      (typeof window !== 'undefined' &&
        (PUBLIC_PAGE_PATH.test(window.location?.pathname ?? '') ||
          isPublicEventPath(window.location?.pathname ?? '')))
      ? null
      : getPendingPathFromUrl(),
  )
  // True from the moment that profile or page is opened until the next logout.
  const profileOpenedRef = useRef(false)
  const wasLoggedRef = useRef(isUserLogged)

  // Bootstrap FCM once the user is authenticated. No-op on web.
  useFcm()
  
  useEffect(() => {
    // If extension mode is detected after initial mount (web hydration / deep link),
    // route through Auth if not logged, then go back to Extension with the same params.
    if (isExtension) {
      // Preserve URL params like: #/extension/:profileId/:payload
      const payload = (hash || '').startsWith('#/extension/')
        ? (hash || '').replace('#/extension/', '')
        : []
      
      const params = {
        profileId: '',
        payload,
      }

      const state = navigation.getState?.()
      const currentIndex =
        state?.index !== undefined && state?.routes?.[state.index]
          ? state.index
          : 0
      const currentRouteName = state?.routes?.[currentIndex]?.name
      const isOnAuthRoute =
        currentRouteName === 'Auth' ||
        (typeof window !== 'undefined' &&
          (window.location?.hash?.includes('/auth/') ||
            window.location?.pathname?.includes('/auth/')))

      if (!isUserLogged) {
        // Stay on Auth if we're already there (or on any /auth/* path), but keep the extension params.
        if (!isOnAuthRoute) {
          navigation.reset({
            index: 0,
            routes: [
              {
                name: 'Auth',
                params,
              } as never,
            ],
          })
        }
        return
      }

      // Logged in: go to Extension (preserve params for DetailsGhostProfile).
      if (currentRouteName !== 'Extension') {
        navigation.reset({
          index: 0,
          routes: [
            {
              name: 'Extension',
              params,
            } as never,
          ],
        })
      }
      return
    }
    // On web (especially in production builds), navigation state can be briefly undefined
    // during hydration. Guard against it to avoid crashes.
    const state = navigation.getState?.()
    const index =
      state?.index !== undefined && state?.routes?.[state.index]
        ? state.index
        : 0
    const currentRouteName = state?.routes?.[index]?.name
    const isOnAuthRoute =
      currentRouteName === 'Auth' ||
      (typeof window !== 'undefined' &&
        (window.location?.hash?.includes('/auth/') ||
          window.location?.pathname?.includes('/auth/')))

    // Events POC: the event page (QR code / invitation link) and the LinkedIn
    // callback must stay reachable while logged out.
    const isOnPublicRoute =
      currentRouteName === 'PublicEvent' ||
      currentRouteName === 'PublicPrivacyPolicy' ||
      currentRouteName === 'LinkedInCallback' ||
      currentRouteName === 'DemoSwitch' ||
      (typeof window !== 'undefined' &&
        (window.location?.pathname?.startsWith('/e/') ||
          window.location?.pathname === '/privacy' ||
          window.location?.pathname?.startsWith('/linkedin/') ||
          window.location?.pathname === '/demo'))

    // ✅ Keep web deep-links working:
    // - do NOT blindly reset to Tab when logged-in
    // - only redirect when necessary (logout, or after login from Auth)
    // - keep current route if it begins with /auth/ (e.g. forgot-password with token)
    // - keep the reset-password link of the e-mail even when logged in: the
    //   stored session is restored a moment after mount, and sending the
    //   person to Tab then would hide the new-password form
    const authRoute = state?.routes?.[index] as any
    const nestedAuthName =
      authRoute?.state?.routes?.[authRoute.state.index ?? 0]?.name ?? authRoute?.params?.screen
    const isOnResetPassword =
      nestedAuthName === 'ResetPassword' ||
      (typeof window !== 'undefined' &&
        !!window.location?.pathname?.includes('/auth/reset-password'))

    if (!isUserLogged) profileOpenedRef.current = false

    // Only for a visitor who was never logged in on this page load: a logout
    // while a legal page is displayed must still lead to Auth, or its back
    // arrow would return to a logged-in screen. (Not "first run of this
    // effect": it runs again right after mount.)
    if (isUserLogged) wasLoggedRef.current = true
    const isOnPublicPage =
      !wasLoggedRef.current &&
      typeof window !== 'undefined' &&
      PUBLIC_PAGE_PATH.test(window.location?.pathname ?? '')

    // If user logs out, force Auth unless already on an auth route
    if (!isUserLogged && !isOnAuthRoute && !isOnPublicPage && !isOnPublicRoute) {
      navigation.reset({
        index: 0,
        routes: [{ name: 'Auth' } as never],
      })
      return
    }

    // Logged in after opening a shared profile link: open that profile, on top
    // of the contact list so "Retour" still leads somewhere.
    if (isUserLogged && pendingProfileIdRef.current) {
      const profileId = pendingProfileIdRef.current
      pendingProfileIdRef.current = null
      pendingPathRef.current = null
      profileOpenedRef.current = true
      navigation.reset({
        index: 1,
        routes: [
          { name: 'Tab' } as never,
          {
            name: 'Profile',
            params: { screen: 'DetailsGhostProfile', params: { profileId } },
          } as never,
        ],
      })
      return
    }

    // Logged in after opening the address of another page: open that page, on
    // top of the contact list as well. An address that is not a page of the
    // app leads to the contact list below.
    if (isUserLogged && pendingPathRef.current) {
      const path = pendingPathRef.current
      pendingPathRef.current = null
      const target = (linkingConfig.getStateFromPath ?? getStateFromPath)(
        path,
        linkingConfig.config as never,
      )?.routes?.[0]
      if (target && !['Auth', 'NotFound', 'Extension'].includes(target.name)) {
        profileOpenedRef.current = true
        navigation.reset(
          (target.name === 'Tab'
            ? { index: 0, routes: [target] }
            : { index: 1, routes: [{ name: 'Tab' }, target] }) as never,
        )
        return
      }
    }

    // If user logs in while currently on Auth, take them to Tab. Not when the
    // profile or the page of a link was just opened: for a member already logged in this
    // effect runs again before the navigation has left Auth, and going to Tab
    // would close that profile.
    if (isUserLogged && currentRouteName === 'Auth' && !isOnResetPassword) {
      if (profileOpenedRef.current) {
        profileOpenedRef.current = false
        return
      }
      navigation.reset({
        index: 0,
        routes: [{ name: 'Tab' } as never],
      })
      return
    }

    // NOTE: we intentionally do not reset on isExtension here,
    // because it can break deep-linked extension/profile routes.
  }, [hash, isExtension, isUserLogged, navigation])

  return (
    <RootStack.Navigator
      screenOptions={{ headerShown: false }}
      initialRouteName={isExtension ? (isUserLogged ? 'Extension' : 'Auth') : (isUserLogged ? 'Tab' : 'Auth')}
    >
      <RootStack.Screen name="Auth" component={AuthNavigator} />
      <RootStack.Screen name="Tab" component={TabNavigator} />
      <RootStack.Screen name="Contacts" component={ContactNavigator} />
      <RootStack.Screen name="Messages" component={MessagesStackNavigator} />
      <RootStack.Screen name="Profile" component={ProfileNavigator} />
      <RootStack.Screen name="Extension" component={DetailsGhostProfile} />
      <RootStack.Screen name="Menu" component={MenuNavigator} />
      <RootStack.Screen name="PublicEvent" component={PublicEvent} />
      <RootStack.Screen name="MyTicket" component={MyTicket} />
      <RootStack.Screen name="PublicPrivacyPolicy" component={PublicPrivacyPolicy} />
      <RootStack.Screen name="SimulatedPayment" component={SimulatedPayment} />
      <RootStack.Screen name="LinkedInCallback" component={LinkedInCallback} />
      <RootStack.Screen name="DemoSwitch" component={DemoSwitch} />
      <RootStack.Screen name="NotFound" component={NotFound} />
    </RootStack.Navigator>
  )
}
